import React from "react";

import styled from "styled-components";

import { useNetworkMetadata, useUserData } from "Flocknote/apiHooks";
import { useIsLoggedIn } from "Flocknote/stateHooks";

import { useGetHomeSettings } from "../../API/queries";
import { useGetBackgroundPhotoData } from "../../API/queryHooks";
import CroppedImage from "../../shared/CroppedImage";

import GetInvolvedForm from "./GetInvolvedForm";

const HeroSection = () => {
  const { data: homeSettings } = useGetHomeSettings();
  const { backgroundPhoto, isLoading: isPhotoLoading } =
    useGetBackgroundPhotoData();
  const isLoggedIn = useIsLoggedIn();
  const [networkMetadata] = useNetworkMetadata();

  const [userData] = useUserData();

  const { imageUrl, x, y, height, width } = backgroundPhoto || {};

  // Use backend image URL or fallback to placeholder
  const backgroundImageUrl = imageUrl || "";

  // Use backend cropping data or default placeholder values
  const croppedAreaPixels =
    x !== undefined &&
    y !== undefined &&
    width !== undefined &&
    height !== undefined
      ? { x, y, width, height }
      : { width: 2319, height: 887, x: 3587, y: 823 };

  const megaMode = !homeSettings?.aboutUs;

  return (
    <HeroContainer
      $megaMode={megaMode}
      $backgroundColor={homeSettings?.backgroundColor}
      $backgroundGradient={homeSettings?.backgroundGradient}
    >
      {/* Background Image */}
      <BackgroundImageWrapper>
        {!isPhotoLoading && (
          <CroppedImage
            croppedAreaPixels={croppedAreaPixels}
            imageUrl={backgroundImageUrl}
          />
        )}
      </BackgroundImageWrapper>

      <CenteredContent>
        <HeroContent $hasSubheading={!!homeSettings?.subheading}>
          <LeftSection>
            <ChurchInfo>
              <LogoHolder>
                <Logo $logo={networkMetadata?.networkLogo} />
              </LogoHolder>
              <ChurchName>
                {networkMetadata?.networkName
                  ?.split(" ")
                  .map((word, index) =>
                    word.toLowerCase() === "of" ? (
                      <em key={index}>{word} </em>
                    ) : (
                      <span key={index}>{word} </span>
                    )
                  )}
              </ChurchName>
            </ChurchInfo>

            {homeSettings?.subheading && (
              <Headline>
                <HeroText>{homeSettings.subheading}</HeroText>
              </Headline>
            )}
          </LeftSection>

          <GetInvolvedForm
            userData={userData}
            isLoggedIn={isLoggedIn}
            isRecaptchaRequired={homeSettings?.forceRecaptcha}
            showPhone={homeSettings?.showPhone}
            tenDlcNotice={homeSettings?.tenDlcNotice}
            tenDlcCheckbox={homeSettings?.tenDlcCheckbox}
          />
        </HeroContent>
      </CenteredContent>
    </HeroContainer>
  );
};

const HeroContainer = styled.div`
  position: relative;
  min-height: 50vh;
  flex: ${({ $megaMode }) => ($megaMode ? 1 : 0)};
  display: flex;
  justify-content: center;
  overflow: hidden;
`;

const BackgroundImageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;

  /* Make the CroppedImage fill the entire container */
  > div {
    width: 100% !important;
    height: 100% !important;
    border: none !important;
    border-radius: 0 !important;
    background: transparent !important;
  }

  /* Make the canvas fill and cover the container */
  canvas {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
  }

  /* Add overlay for better text readability */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 2;
  }
`;

const CenteredContent = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: ${(props) =>
    props.$hasSubheading ? "space-between" : "center"};
  align-items: center;
  padding: 2rem 4rem;
  gap: 2rem;
  flex: 1;
  max-width: 1200px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
    padding: 1.5rem;
    justify-content: center;
  }

  @media (max-width: 480px) {
    gap: 1.5rem;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  flex: 1;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

const ChurchInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const LogoHolder = styled.div`
  width: 90px;
  flex-shrink: 0;
  flex-grow: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Logo = styled.div`
  border-radius: 100%;
  width: 100%;
  aspect-ratio: 1;
  background-image: url(${({ $logo }) => $logo});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  background-color: white;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.35);
  border: 4px solid white;
  box-sizing: border-box;
  margin-top: -8px;
  margin-bottom: -8px;

  @media print {
    display: none;
  }

  @media (max-width: 768px) {
    margin: 0;
    width: 80px;
  }
`;

const ChurchName = styled.h1`
  font-size: 2rem;
  font-weight: 500;
  color: white;
  line-height: 1.1;
`;

const Headline = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  color: white;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const HeroText = styled.h1`
  font-size: 3rem;
  font-weight: bold;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }

  @media (max-width: 360px) {
    font-size: 1.5rem;
  }
`;

export default HeroSection;
